import { useMuiModalContext } from '@modules/common/components/Modal/ModalContext';
import { ConfirmAutoApproveOffDialog } from '@modules/users/components/AutoApproveUsersToggle/ConfirmAutoApproveOffDialog';
import { useToggleTenantAutoApproval } from '@modules/users/hooks';
import { useUserManagementStore } from '@modules/users/store/hooks';
import { UserManagementActionType } from '@modules/users/store/userManagementActionType';
import { trackUserManagementEvents } from '@modules/workspaceSettings/utils';
import { InfoOutlined } from '@mui/icons-material';
import {
  FormControlLabel,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import { ChangeEvent } from 'react';

export const AutoApproveUsersToggle = () => {
  const { openModal, closeModal } = useMuiModalContext();

  const isAutoApprovalDisabled = useUserManagementStore(
    ({ isAutoApprovalDisabled }) => isAutoApprovalDisabled,
  );
  const isFetchStateRequestInProgress = useUserManagementStore(
    ({ visual }) => visual.isFetchingAutoApprovalState,
  );
  const dispatch = useUserManagementStore(({ dispatch }) => dispatch);

  const { toggleAutoApproval, isLoading: isToggleRequestInProgress } =
    useToggleTenantAutoApproval({
      onError: () => {
        dispatch({
          type: UserManagementActionType.SET_AUTO_APPROVAL_STATE,
          payload: {
            isAutoApprovalDisabled: !isAutoApprovalDisabled,
          },
        });
      },
    });

  const handleChange = (_: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    const isDisabled = !checked;

    if (isDisabled) {
      openModal({
        contentComponent: () => (
          <ConfirmAutoApproveOffDialog
            onConfirm={() => {
              toggleAutoApproval(isDisabled);
              dispatch({
                type: UserManagementActionType.SET_AUTO_APPROVAL_STATE,
                payload: {
                  isAutoApprovalDisabled: isDisabled,
                },
              });
              closeModal();
              trackUserManagementEvents({
                eventName: 'User Management - Auto Approval Toggled Off',
              });
            }}
            onCancel={() => closeModal()}
          />
        ),
        modalProps: {
          fullWidth: true,
          maxWidth: 'sm',
          PaperProps: {
            elevation: 3,
          },
        },
      });
      return;
    }

    toggleAutoApproval(isDisabled);
    dispatch({
      type: UserManagementActionType.SET_AUTO_APPROVAL_STATE,
      payload: {
        isAutoApprovalDisabled: isDisabled,
      },
    });
    trackUserManagementEvents({
      eventName: 'User Management - Auto Approval Toggled On',
    });
  };

  const renderInfoTooltip = () => {
    return (
      <Tooltip
        arrow
        placement="top"
        title={
          <Stack spacing={1}>
            <Typography variant="caption">
              When auto-approval is enabled, new users from your organization
              will be automatically approved during sign-up.
            </Typography>
            <Typography variant="caption">
              When disabled, they will need to be invited by an existing user.
            </Typography>
          </Stack>
        }
      >
        <InfoOutlined
          fontSize="small"
          sx={{
            color: 'text.secondary',
          }}
        />
      </Tooltip>
    );
  };

  const isLoading = isToggleRequestInProgress || isFetchStateRequestInProgress;

  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      {renderInfoTooltip()}
      <FormControlLabel
        control={
          <Switch
            checked={!isAutoApprovalDisabled}
            onChange={handleChange}
            size="small"
            sx={{ ml: 1 }}
          />
        }
        disabled={isLoading}
        label={<Typography variant="body1">Auto-approve new users</Typography>}
        labelPlacement="start"
      />
    </Stack>
  );
};
